import {
  DataRoom,
  DataRoomSearchData,
} from 'router/subrouters/DataRoom/models/DataRoom';
import httpClient from '../httpClient';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { DataRoomMember } from '../../models/DataRoomMember';
import { Params } from 'react-router-dom';
import { FilePreview } from 'models/File';
import {
  ColdLeadAggregationResult,
  ColdLeadsBroadcastPayload,
  DataRoomDashboardTimeframe,
  DataRoomVisitStat,
  HotLeadAggregationResult,
  SuggestedActionsResponse,
} from '../../models/Dashboard';
import { SuggestionPage } from 'models/SuggestedActions';

function dataRoomLogout(dataRoomId: string) {
  return httpClient.post(`data-rooms/${dataRoomId}/logout`);
}

function getRoom(dataRoomId: string) {
  return httpClient.get<DataRoom>(`data-rooms/${dataRoomId}`);
}

function patchDataRoom(
  dataRoomId: string,
  data: Partial<DataRoom>,
  isDefault: boolean, // apply for all DR
) {
  return httpClient.patch<DataRoom>(`data-rooms/${dataRoomId}`, {
    ...data,
    useDefaults: isDefault,
  });
}

function getMembers(dataRoomId: string, params?: Params) {
  return httpClient.get<PaginatedData<DataRoomMember>>(
    `data-rooms/${dataRoomId}/by-members`,
    { params },
  );
}

function getExportedIndex(dataRoomId: string) {
  return httpClient.get<FilePreview>(`data-rooms/${dataRoomId}/export-index`);
}

function addQuestionSubject(dataRoomId: string, name: string) {
  return httpClient.post(`data-rooms/${dataRoomId}/question-subject/add`, {
    name,
  });
}

function editQuestionSubject(
  dataRoomId: string,
  newName: string,
  oldName: string,
) {
  return httpClient.post(`data-rooms/${dataRoomId}/question-subject/edit`, {
    newName,
    oldName,
  });
}

function removeQuestionSubject(dataRoomId: string, name: string) {
  return httpClient.post(`data-rooms/${dataRoomId}/question-subject/remove`, {
    name,
  });
}

function toggleQa(dataRoomId: string, isDisabledQA: boolean) {
  return httpClient.post(`data-rooms/${dataRoomId}/toggle-qa`, {
    isDisabledQA,
  });
}

function closeDataRoom(dataRoomId: string, isClosed: boolean) {
  return httpClient.post(`data-rooms/${dataRoomId}/close`, {
    isClosed,
  });
}

function disableDataRoom(dataRoomId: string, isDisabled: boolean) {
  return httpClient.post(`data-rooms/${dataRoomId}/disable`, {
    isDisabled,
  });
}

function searchDataRoom(
  dataRoomId: string,
  searchString: string,
  pageUrl: string,
) {
  return httpClient.get<DataRoomSearchData>(`data-rooms/${dataRoomId}/search`, {
    params: { searchString, pageUrl },
  });
}

function updateGlobalSecuritySettings(
  dataRoomId: string,
  data: Partial<DataRoom>,
  applyOnExistingFiles: boolean,
  isDefault: boolean, // apply for all DR
) {
  return httpClient.post<DataRoom>(
    `data-rooms/${dataRoomId}/global-security-settings`,
    { ...data, applyOnExistingFiles, useDefaults: isDefault },
  );
}

function undoCompleteOnboarding(dataRoomId: string) {
  return httpClient.post<DataRoom>(
    `data-rooms/${dataRoomId}/undo-complete-onboarding`,
  );
}

function getStorageCapacityUtilization(dataRoomId: string) {
  return httpClient.get<{ storageCapacityUtilization: number }>(
    `data-rooms/${dataRoomId}/storage-capacity-utilization`,
  );
}

function getHotLeads(
  dataRoomId: string,
  params?: PaginationParams & { timeframe?: DataRoomDashboardTimeframe },
) {
  return httpClient.get<PaginatedData<HotLeadAggregationResult>>(
    `data-rooms/${dataRoomId}/hot-leads`,
    { params },
  );
}

function getColdLeads(
  dataRoomId: string,
  params?: PaginationParams & { timeframe?: DataRoomDashboardTimeframe },
) {
  return httpClient.get<PaginatedData<ColdLeadAggregationResult>>(
    `data-rooms/${dataRoomId}/cold-leads`,
    { params },
  );
}

function getVisitStats(dataRoomId: string) {
  return httpClient.get<Array<DataRoomVisitStat>>(
    `data-rooms/${dataRoomId}/visit-stats`,
  );
}

function coldLeadsBroadcast(
  dataRoomId: string,
  data: ColdLeadsBroadcastPayload,
) {
  return httpClient.post(`data-rooms/${dataRoomId}/cold-leads-broadcast`, data);
}

function trackUploadStart(dataRoomId: string) {
  return httpClient.post(`data-rooms/${dataRoomId}/track-upload-start`);
}

function getSuggestedActions(dataRoomId: string) {
  return httpClient.get<SuggestedActionsResponse>(
    `data-rooms/${dataRoomId}/suggested-actions`,
  );
}

function muteSuggestedAction(
  dataRoomId: string,
  actionType: string,
  suggestionPage: SuggestionPage,
  duration?: number,
) {
  return httpClient.post(
    `data-rooms/${dataRoomId}/suggested-actions/${actionType}/mute`,
    { duration, suggestionPage },
  );
}

function restoreMutedSuggestedAction(dataRoomId: string, actionType: string) {
  return httpClient.post(
    `data-rooms/${dataRoomId}/suggested-actions/${actionType}/restore`,
  );
}

function actionedSuggestedAction(
  dataRoomId: string,
  data: {
    suggestionName: string;
    suggestionAction: string;
    suggestionPage?: SuggestionPage;
  },
) {
  return httpClient.post(
    `data-rooms/${dataRoomId}/suggested-actions/action`,
    data,
  );
}

function updatePreferredValues(
  dataRoomId: string,
  data: Pick<
    DataRoomMember,
    | 'isPrefferedSummarisedView'
    | 'preferredAnalyticsTimeframe'
    | 'preferredDashboardTimeframe'
  >,
) {
  return httpClient.post(`data-rooms/${dataRoomId}/preferred-values`, data);
}

export default {
  dataRoomLogout,
  getRoom,
  patchDataRoom,
  getMembers,
  getExportedIndex,
  addQuestionSubject,
  editQuestionSubject,
  removeQuestionSubject,
  toggleQa,
  closeDataRoom,
  disableDataRoom,
  searchDataRoom,
  updateGlobalSecuritySettings,
  undoCompleteOnboarding,
  getStorageCapacityUtilization,
  getHotLeads,
  getColdLeads,
  getVisitStats,
  coldLeadsBroadcast,
  trackUploadStart,
  getSuggestedActions,
  muteSuggestedAction,
  restoreMutedSuggestedAction,
  actionedSuggestedAction,
  updatePreferredValues,
};
